import { Group, UnstyledButton } from '@mantine/core';
import { hideNotification, showNotification } from '@mantine/notifications';
import i18next from 'i18next';
import {
  APP_VERSION_MANIFEST_PATH,
  APP_VERSION_NOTIFICATION_ID,
  CHUNK_RELOAD_SESSION_KEY,
} from '@/constants/appVersion';
import type { AppVersionManifest } from '@/types';

let hasShownVersionNotification = false;

export const getInitialBuildId = (): string => __APP_BUILD_ID__;

export const fetchAppVersionManifest = async (): Promise<AppVersionManifest> => {
  const url = `${APP_VERSION_MANIFEST_PATH}?t=${Date.now()}`;
  const response = await fetch(url, { cache: 'no-store' });

  if (!response.ok) {
    throw new Error('Failed to fetch app version manifest');
  }

  return response.json() as Promise<AppVersionManifest>;
};

export const reloadApp = (): void => {
  window.location.reload();
};

export const notifyAppVersionUpdate = (): void => {
  if (hasShownVersionNotification) {
    return;
  }

  hasShownVersionNotification = true;

  const handleReloadClick = (): void => {
    hideNotification(APP_VERSION_NOTIFICATION_ID);
    hasShownVersionNotification = false;
    reloadApp();
  };

  showNotification({
    id: APP_VERSION_NOTIFICATION_ID,
    title: (
      <Group justify="space-between" gap="md" wrap="nowrap">
        {i18next.t('appVersion.updateTitle', { ns: 'global' })}
        <UnstyledButton c="brand.6" fz="sm" onClick={handleReloadClick}>
          {i18next.t('appVersion.reloadButton', { ns: 'global' })}
        </UnstyledButton>
      </Group>
    ),
    message: i18next.t('appVersion.updateMessage', { ns: 'global' }),
    color: 'blue',
    autoClose: false,
    withCloseButton: false,
  });
};

export const isChunkLoadError = (error: unknown): boolean => {
  const message = error instanceof Error ? error.message : String(error);

  return (
    message.includes('Failed to fetch dynamically imported module') ||
    message.includes('Loading chunk') ||
    message.includes('ChunkLoadError') ||
    message.includes('Importing a module script failed')
  );
};

export const isScriptLoadError = (event: ErrorEvent): boolean => {
  const message = event.message || '';

  return (
    message.includes('MIME type') ||
    message.includes('module script') ||
    message.includes('Failed to load module script')
  );
};

export const hasReloadedChunkOnce = (): boolean => {
  try {
    return sessionStorage.getItem(CHUNK_RELOAD_SESSION_KEY) === '1';
  } catch {
    return true;
  }
};

export const markReloadedChunkOnce = (): void => {
  try {
    sessionStorage.setItem(CHUNK_RELOAD_SESSION_KEY, '1');
  } catch (_error) {
    void _error;
  }
};

export const handleStaleAssetFailure = (): void => {
  if (!hasReloadedChunkOnce()) {
    markReloadedChunkOnce();
    reloadApp();
    return;
  }

  notifyAppVersionUpdate();
};

export const importWithRetry = async <T,>(importer: () => Promise<T>): Promise<T> => {
  try {
    return await importer();
  } catch (_error) {
    try {
      return await importer();
    } catch (error2) {
      if (typeof window !== 'undefined' && isChunkLoadError(error2)) {
        handleStaleAssetFailure();

        return new Promise<T>(() => {});
      }

      throw error2;
    }
  }
};
